import React, {useState} from 'react'
import {
  Button,
  DateTimeRangePicker,
  Icon,
  Loading,
  Modal,
  Select,
} from '@therms/atalaya'
import {MdClose} from 'react-icons/md'
import {
  Alerts,
  DAY_LABELS,
  daysOfWeekFrom,
  EditableAvailability,
  formatUnavailableTimes,
  makeBlockKey,
  SchedulingUserAvailability,
  toEditableAvailability,
  toSavedAvailability,
  UnavailableTimeBlock,
} from '@therms/react-modules'

interface EditAvailabilityModalProps {
  availability?: SchedulingUserAvailability
  closeHandler: () => void
  /** The region's first day of the week; the day list starts on it. */
  firstDayOfWeek?: string
  onSubmit: (draft: SchedulingUserAvailability) => Promise<void>
  /**
   * When the device's clock differs from the primary region's: how a block
   * converts, and that region's name, so each block shows what is saved.
   */
  storedTimes?: {
    convert: (blocks: UnavailableTimeBlock[]) => UnavailableTimeBlock[] | undefined
    zoneLabel: React.ReactNode
  }
}

// The picker drops any edit made while its other side is empty, so a block
// switched to a specific time starts with a full range to adjust.
const DEFAULT_SPECIFIC_TIME = {end: '17:00', start: '09:00'}

export function EditAvailabilityModal({
  availability,
  closeHandler,
  firstDayOfWeek,
  onSubmit,
  storedTimes,
}: EditAvailabilityModalProps) {
  const week = daysOfWeekFrom(firstDayOfWeek)
  const dayOptions = week.map((day) => ({label: DAY_LABELS[day], value: day}))
  const [loading, setLoading] = useState(false)
  const [draft, setDraft] = useState<EditableAvailability>(() =>
    toEditableAvailability(availability),
  )

  const unavailableTimes = draft.unavailableTimes ?? []

  function updateBlock(key: string, patch: Partial<UnavailableTimeBlock>) {
    setDraft((prev) => ({
      ...prev,
      unavailableTimes: (prev.unavailableTimes ?? []).map((block) =>
        block.key === key ? {...block, ...patch} : block,
      ),
    }))
  }

  function removeBlock(key: string) {
    setDraft((prev) => ({
      ...prev,
      unavailableTimes: (prev.unavailableTimes ?? []).filter(
        (block) => block.key !== key,
      ),
    }))
  }

  function addBlock() {
    setDraft((prev) => ({
      ...prev,
      unavailableTimes: [
        ...(prev.unavailableTimes ?? []),
        {allDay: true, day: week[0], key: makeBlockKey()},
      ],
    }))
  }

  async function handleSubmit() {
    setLoading(true)
    try {
      await onSubmit(toSavedAvailability(draft))
      closeHandler()
    } catch (err: any) {
      Alerts.error('There was a problem submitting your changes', err.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      closeHandler={closeHandler}
      header="Edit unavailable times"
      width="md"
    >
      <div className="gap-base relative flex flex-col">
        {loading && <Loading overlay size="lg" />}

        <div className="bg-surface-subtle border-border gap-base p-sm flex flex-col rounded-lg border">
          <div className="gap-sm flex flex-col">
            <span className="text-color-subtle text-xs font-semibold uppercase opacity-80">
              Unavailable times
            </span>
            {storedTimes && (
              <span className="text-color-subtle text-xs">
                Times are your time. They follow your primary region&apos;s
                clock, so they may shift by an hour when daylight saving
                changes.
              </span>
            )}
            {unavailableTimes.map((block) => (
              <div key={block.key} className="gap-xxs flex flex-col">
              <div className="gap-sm flex items-center">
                <div className="w-[140px] flex-none">
                  <Select
                    isSearchable={false}
                    onChangeValue={(day) => updateBlock(block.key, {day})}
                    options={dayOptions}
                    value={block.day}
                  />
                </div>
                {block.allDay ? (
                  <div className="gap-sm flex flex-1 items-center">
                    <span className="bg-surface-strong border-border px-sm py-xxs rounded-md border text-sm font-semibold">
                      All day
                    </span>
                    <Button
                      onClick={() =>
                        updateBlock(block.key, {
                          allDay: false,
                          ...DEFAULT_SPECIFIC_TIME,
                        })
                      }
                      size="xs"
                      subtle
                      variant="main"
                    >
                      Set specific time
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="min-w-0 flex-1">
                      {/* Clock times, not instants: a block is a rule about
                          a weekday. An end at or before the start runs into
                          the next day, and is saved as one overnight block. */}
                      <DateTimeRangePicker
                        allowOvernight
                        minuteStep={15}
                        mode="time"
                        onChangeValue={({end, start}) =>
                          updateBlock(block.key, {
                            end: end ?? block.end,
                            start: start ?? block.start,
                          })
                        }
                        onChangeValueBehavior="StartOrEnd"
                        showClearValueButton={false}
                        value={{end: block.end, start: block.start}}
                        valueFormat="HH:mm"
                      />
                    </div>
                    <Button
                      className="whitespace-nowrap"
                      onClick={() =>
                        updateBlock(block.key, {
                          allDay: true,
                          end: undefined,
                          start: undefined,
                        })
                      }
                      size="xs"
                      subtle
                      variant="main"
                    >
                      All day
                    </Button>
                  </>
                )}
                <Button
                  onClick={() => removeBlock(block.key)}
                  size="xs"
                  subtle
                  title="Remove block"
                  variant="critical"
                >
                  <Icon Icon={MdClose} size="sm" />
                </Button>
              </div>
              {storedTimes && (
                <span className="text-color-subtle text-xs">
                  {formatUnavailableTimes(
                    storedTimes.convert([block]) ?? [],
                  ) || 'Not saved'}{' '}
                  {storedTimes.zoneLabel} time
                </span>
              )}
              </div>
            ))}
            {!unavailableTimes.length && (
              <div className="text-color-subtle text-sm italic">
                No blocked times added.
              </div>
            )}
            <Button onClick={addBlock} outline size="xs" variant="neutral">
              + Add block
            </Button>
          </div>
        </div>

        <div className="gap-sm flex items-center justify-end">
          <Button
            disabled={loading}
            onClick={closeHandler}
            outline
            variant="neutral"
          >
            Cancel
          </Button>
          <Button disabled={loading} onClick={handleSubmit} variant="main">
            Submit changes
          </Button>
        </div>
      </div>
    </Modal>
  )
}
